import React from 'react';

import {Anchor, Heading, Text} from '@albert-io/atomic';
import albeyHappy from 'assets/images/albey/albey_expressions_Happy.svg';
import {CleverMark} from 'client/Clever/CleverMark';
import type {CleverUser} from 'client/Clever/util';

import {BOOK_TRAINING_LINK, GETTING_STARTED_ARTICLE_URL} from '../gettingStartedResources';
import {GettingStartedItem} from '../cards/GettingStartedItem';
import {GettingStartedVideo} from '../cards/GettingStartedVideo';
import {useCleverSync} from '../hooks/useCleverSync';
import {useCleverUser} from '../hooks/useCleverUser';
import {useNewClassroomModal} from '../hooks/useNewClassroomModal';

/**
 * Clever roster-sync entry (no-classes setup). Isolated so the sync hook only runs when the teacher is Clever-eligible.
 */
const WelcomeCleverRow = ({user}: {user: CleverUser}) => {
  const {handleClick, modals} = useCleverSync(user);
  return (
    <>
      <GettingStartedItem
        chipClassName='background-blueberry-subtle content-blueberry'
        data-testid='home-action__import-clever'
        label='Sync your roster and classes with Clever.'
        leading={<CleverMark />}
        onClick={handleClick}
        trailingIcon='arrows-rotate'
      />
      {modals}
    </>
  );
};

export const WelcomeCard = () => {
  const {user, showClever} = useCleverUser();
  const {openNewClassroom, newClassroomModal} = useNewClassroomModal();

  return (
    <section aria-label='Welcome' className='rounded-x3 background-primary mt-x2 flex flex-col'>
      <div className='gap-x2 py-x3 px-x5 border-border-primary flex items-center border-b'>
        <img alt='Albey Happy' className='w-[72px] rotate-354' src={albeyHappy} />
        <div className='gap-x0-half flex flex-col'>
          <Heading as='h1' className='m-x0 text-[32px]!' size='m'>
            Welcome!
          </Heading>
          <Text color='secondary'>Check out our helpful links to get started</Text>
        </div>
      </div>

      <div className='gap-x3 p-x5 m:flex-row flex flex-col'>
        <GettingStartedVideo className='m:w-1/2' />
        <div className='gap-x3 m:w-1/2 flex flex-col'>
          <div className='gap-x1-half flex flex-col'>
            <div className='gap-x2 flex items-center justify-between'>
              <Text>Classroom Set-Up</Text>
              <Anchor
                as='a'
                className='text-s'
                href={GETTING_STARTED_ARTICLE_URL}
                noUnderline
                rel='noopener noreferrer'
                target='_blank'
              >
                Learn more
              </Anchor>
            </div>
            {showClever && user ? <WelcomeCleverRow user={user} /> : null}
            <GettingStartedItem
              chipClassName='background-kiwi-subtle content-kiwi'
              data-testid='home-action__create-class'
              icon='chalkboard'
              iconStyle='light'
              label='Create Class'
              onClick={openNewClassroom}
              trailingIcon='plus'
            />
          </div>
          <GettingStartedItem
            chipClassName='background-dragonfruit-subtle content-dragonfruit'
            href={BOOK_TRAINING_LINK.href}
            icon={BOOK_TRAINING_LINK.icon}
            iconStyle='light'
            label={BOOK_TRAINING_LINK.label}
          />
        </div>
      </div>

      {newClassroomModal}
    </section>
  );
};
